import { useState, useCallback, useRef, useEffect } from "react";
import { CallBackProps, STATUS } from "react-joyride";
import { useTranslation } from "react-i18next";
import {
  InteractiveTourStep,
  TourState,
  TourConfig,
  BaseActionConfig,
} from "../types/tour";
import { TourActionDetection } from "../services/tourActionDetection";
import { TourPersistence } from "../services/tourPersistence";
import { TourTelemetry } from "../services/tourTelemetry";
import { logger } from "../utils/logger";

interface UseInteractiveTourProps {
  onTourComplete?: () => void;
  onTourSkip?: () => void;
  onStepComplete?: (stepId: string) => void;
  userId?: string;
  config?: Partial<TourConfig>;
}

export const useInteractiveTour = ({
  onTourComplete,
  onTourSkip,
  onStepComplete,
  userId,
  config = {},
}: UseInteractiveTourProps = {}) => {
  const { t } = useTranslation();

  const defaultConfig: TourConfig = {
    version: "1.0.0",
    skipOnMobile: false,
    autoStart: false,
    showProgress: true,
    showSkipButton: true,
    continuous: true,
    disableOverlayClose: true,
    spotlightClicks: true,
    locale: "pt-BR",
    ...config,
  };

  const [state, setState] = useState<TourState>({
    isRunning: false,
    currentStep: 0,
    steps: [],
    isWaitingForInteraction: false,
    completedInteractions: new Set(),
    tourVersion: defaultConfig.version,
    userId,
  });

  const actionDetection = useRef(new TourActionDetection());
  const currentStepRef = useRef<number>(0);
  const isInitializedRef = useRef(false);

  // Criar steps do tour
  const createTourSteps = useCallback(
    (): InteractiveTourStep[] => [
      {
        id: "welcome-download",
        target: "#jess-welcome-download-button",
        content: t("interactiveTour.welcomeDownload.content"),
        title: t("interactiveTour.welcomeDownload.title"),
        placement: "top",
        disableBeacon: true,
        hideCloseButton: false,
        spotlightClicks: true,
        data: {
          requiresInteraction: false, // Mudado para false para permitir progressão livre
          manualProgression: true, // Progressão controlada manualmente no ChatJessPage
        },
      },
      {
        id: "step-by-step",
        target: "#jess-step-by-step-button",
        content: t("interactiveTour.stepByStep.content"),
        title: t("interactiveTour.stepByStep.title"),
        placement: "top",
        disableBeacon: true,
        hideCloseButton: false,
        spotlightClicks: true,
        data: {
          requiresInteraction: true,
          requiredAction: "click",
          targetSelector: "#jess-step-by-step-button",
        },
      },
      {
        id: "step-by-step-panel",
        target: "#jess-step-by-step-panel",
        content: t("interactiveTour.stepByStepPanel.content"),
        title: t("interactiveTour.stepByStepPanel.title"),
        placement: "left",
        disableBeacon: true,
        hideCloseButton: false,
        spotlightClicks: true, // Permitir cliques no painel para scroll
        data: {
          requiresInteraction: true,
          requiredAction: "click",
          targetSelector: "#jess-step-by-step-next-button", // Botão "Próximo" no final do painel
          allowScrollInTarget: true, // Permitir scroll no painel destacado
          openPanelFn: () => {
            // Função para abrir o painel se não estiver aberto
            const panelElement = document.getElementById(
              "jess-step-by-step-panel",
            );
            if (panelElement && !panelElement.classList.contains("open")) {
              // Simular clique no botão "Passo a Passo" para abrir o painel
              const stepByStepButton = document.getElementById(
                "jess-step-by-step-button",
              );
              if (stepByStepButton) {
                stepByStepButton.click();
              }
            }
          },
        },
      },
      {
        id: "qr-code-button",
        target: "#dashboard-qr-code-button",
        content: t("interactiveTour.qrCodeButton.content"),
        title: t("interactiveTour.qrCodeButton.title"),
        placement: "left",
        disableBeacon: true,
        hideCloseButton: false,
        spotlightClicks: true,
        disableOverlay: false,
        data: {
          requiresInteraction: true,
          requiredAction: "click",
          targetSelector: "#dashboard-qr-code-button",
        },
      },
      {
        id: "completion",
        target: "body",
        content: t("interactiveTour.completion.content"),
        title: t("interactiveTour.completion.title"),
        placement: "center",
        disableBeacon: true,
        hideCloseButton: false,
        spotlightClicks: false,
        data: {
          requiresInteraction: false,
          autoFinish: true,
        },
      },
    ],
    [t],
  );

  // Verificar se o tour deve ser iniciado automaticamente
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;

      // Migrar dados legados
      TourPersistence.migrateLegacyData(userId);

      // Aguardar DOM estar pronto antes de verificar estado do tour
      setTimeout(() => {
        // Verificar se há um tour em andamento
        const savedState = TourPersistence.loadTourState(userId);
        if (
          savedState &&
          savedState.currentStep !== undefined &&
          !savedState.completed
        ) {
          // Restaurar tour em andamento
          logger.info(
            `[InteractiveTour] Restaurando tour em andamento - Step: ${savedState.currentStep}`,
          );
          const steps = createTourSteps();

          // Verificar se o elemento target do step atual existe
          const currentStep = steps[savedState.currentStep];
          if (currentStep) {
            let checkAttempts = 0;
            const maxAttempts = 10; // Máximo 5 segundos esperando

            const checkElementExists = () => {
              checkAttempts++;
              const targetElement = document.querySelector(currentStep.target);

              if (targetElement || currentStep.target === "body") {
                // Elemento existe, pode restaurar o tour
                setState((prev: TourState) => ({
                  ...prev,
                  isRunning: true,
                  currentStep: savedState.currentStep || 0,
                  steps,
                  totalSteps: steps.length,
                  completedSteps: savedState.completedSteps || [],
                  isWaitingForInteraction: false,
                }));

                // Verificação adicional para o step do QR Code
                if (currentStep.id === "qr-code-button") {
                  // Aguardar um pouco mais e verificar novamente
                  setTimeout(() => {
                    const element = document.querySelector(
                      "#dashboard-qr-code-button",
                    );

                    // Dispatch evento customizado para debug
                    window.dispatchEvent(
                      new CustomEvent("tour-qr-step-ready", {
                        detail: {
                          step: savedState.currentStep,
                          element: !!element,
                        },
                      }),
                    );
                  }, 500);
                }
              } else if (checkAttempts < maxAttempts) {
                // Elemento não existe ainda, aguardar mais um pouco
                setTimeout(checkElementExists, 500);
              } else {
                // Não conseguiu encontrar o elemento após várias tentativas
                logger.warn(
                  `❌ Elemento ${currentStep.target} não encontrado após ${maxAttempts} tentativas. Abortando restauração do tour.`,
                );
                // Limpar estado do tour para evitar loops
                TourPersistence.clearTourState(userId);
              }
            };

            checkElementExists();
          }
        } else if (
          !TourPersistence.hasUserInteractedWithTour(userId) &&
          defaultConfig.autoStart
        ) {
          // Iniciar novo tour
          setTimeout(() => {
            startTour();
          }, 2000); // Aguardar 2s para a página carregar
        }
      }, 1000); // Delay inicial para aguardar componentes renderizarem
    }
  }, []);

  // Iniciar tour
  const startTour = useCallback(() => {
    logger.info("[InteractiveTour] Iniciando tour interativo");

    const steps = createTourSteps();

    setState((prev: TourState) => ({
      ...prev,
      isRunning: true,
      currentStep: 0,
      steps,
      isWaitingForInteraction: false,
      completedInteractions: new Set(),
    }));

    currentStepRef.current = 0;

    // Telemetria
    TourTelemetry.trackTourStart(defaultConfig.version, userId);

    // Salvar progresso inicial
    TourPersistence.saveTourProgress(0, [], userId);
  }, [createTourSteps, defaultConfig.version, userId]);

  // Parar tour
  const stopTour = useCallback(() => {
    logger.info("[InteractiveTour] Parando tour interativo");

    setState((prev: TourState) => ({
      ...prev,
      isRunning: false,
      currentStep: 0,
      isWaitingForInteraction: false,
    }));

    // Cleanup de detecção de ações
    actionDetection.current.cleanupAll();

    currentStepRef.current = 0;
  }, []);

  // Avançar para próximo step
  const nextStep = useCallback(() => {
    setState((prev: TourState) => {
      const nextStepIndex = prev.currentStep + 1;

      if (nextStepIndex >= prev.steps.length) {
        // Tour completo
        TourPersistence.markTourCompleted(userId);
        TourTelemetry.trackTourFinish(defaultConfig.version, userId);

        onTourComplete?.();

        return {
          ...prev,
          isRunning: false,
          currentStep: 0,
          isWaitingForInteraction: false,
        };
      }

      currentStepRef.current = nextStepIndex;

      // Salvar progresso
      const completedStepIds = prev.steps
        .slice(0, nextStepIndex)
        .map((step: InteractiveTourStep) => step.id);
      TourPersistence.saveTourProgress(nextStepIndex, completedStepIds, userId);

      return {
        ...prev,
        currentStep: nextStepIndex,
        isWaitingForInteraction: false,
      };
    });
  }, [onTourComplete, defaultConfig.version, userId]);

  // Voltar para step anterior
  const previousStep = useCallback(() => {
    setState((prev: TourState) => {
      const prevStepIndex = Math.max(0, prev.currentStep - 1);
      currentStepRef.current = prevStepIndex;

      // Cleanup da detecção atual
      actionDetection.current.cleanup(prev.steps[prev.currentStep]?.id || "");

      return {
        ...prev,
        currentStep: prevStepIndex,
        isWaitingForInteraction: false,
      };
    });
  }, []);

  // Pular tour
  const skipTour = useCallback(() => {
    logger.info("[InteractiveTour] Tour pulado pelo usuário");

    TourPersistence.markTourSkipped(userId);
    TourTelemetry.trackTourSkip(
      defaultConfig.version,
      userId,
      state.currentStep,
    );

    onTourSkip?.();
    stopTour();
  }, [onTourSkip, stopTour, defaultConfig.version, userId, state.currentStep]);

  // Configurar detecção de ação para o step atual
  const setupActionDetection = useCallback(
    (step: InteractiveTourStep) => {
      if (!step.data?.requiresInteraction || !step.data.requiredAction) {
        return;
      }

      // Se o step tem progressão manual, não configurar detecção automática
      if (step.data.manualProgression) {
        logger.debug(
          `[InteractiveTour] Step ${step.id} usa progressão manual - pulando detecção automática`,
        );
        return;
      }

      const { requiredAction, targetSelector } = step.data;

      // Verificar se o elemento existe
      if (
        targetSelector &&
        !TourActionDetection.elementExists(targetSelector)
      ) {
        logger.warn(
          `[InteractiveTour] Elemento não encontrado: ${targetSelector}`,
        );
        // Pular step se elemento não existir
        setTimeout(() => {
          TourTelemetry.trackTourError(
            `Target element not found: ${targetSelector}`,
            defaultConfig.version,
            userId,
            step.id,
            state.currentStep,
          );
          nextStep();
        }, 1000);
        return;
      }

      const actionConfig: BaseActionConfig = {
        selector: targetSelector!,
        validationFn: step.data.validationFn,
        ...(requiredAction === "open" && {
          checkAttribute: "aria-expanded",
          checkClass: "open",
        }),
        ...(requiredAction === "inputFilled" && step.data.inputValidation),
        ...(requiredAction === "navigation" && step.data.navigationTarget),
      };

      const onActionCompleted = () => {
        setState((prev: TourState) => {
          const newCompletedInteractions = new Set(prev.completedInteractions);
          newCompletedInteractions.add(`${prev.currentStep}-${requiredAction}`);

          return {
            ...prev,
            completedInteractions: newCompletedInteractions,
            isWaitingForInteraction: false,
          };
        });

        // Telemetria
        TourTelemetry.trackStepActionCompleted(
          step.id,
          state.currentStep,
          requiredAction,
          defaultConfig.version,
          userId,
        );

        // Callback para step completado
        onStepComplete?.(step.id);

        // Auto-avançar após 1.5s
        setTimeout(() => {
          nextStep();
        }, 1500);
      };

      // Iniciar detecção
      actionDetection.current.detectAction(
        requiredAction,
        actionConfig,
        onActionCompleted,
        step.id,
      );

      setState((prev: TourState) => ({
        ...prev,
        isWaitingForInteraction: true,
      }));
    },
    [nextStep, defaultConfig.version, userId, state.currentStep],
  );

  // Callback do Joyride
  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, action, type, index, step } = data;

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        if (status === STATUS.SKIPPED) {
          skipTour();
        } else {
          // Tour finalizado naturalmente
          TourPersistence.markTourCompleted(userId);
          TourTelemetry.trackTourFinish(defaultConfig.version, userId);
          onTourComplete?.();
        }
        stopTour();
        return;
      }

      if (type === "step:after") {
        const currentStep = state.steps[index];

        if (currentStep) {
          // Telemetria de visualização do step
          TourTelemetry.trackStepView(
            currentStep.id,
            index,
            defaultConfig.version,
            userId,
          );

          // Se é o step de completion, finalizar automaticamente após 2 segundos
          if (currentStep.id === "completion" || currentStep.data?.autoFinish) {
            setTimeout(() => {
              // Debug estado ANTES da finalização
              setState((prevState: TourState) => {
                return prevState; // Não alterar ainda, só para debug
              });

              // Finalizar tour ANTES de chamar callbacks
              setState((prev: TourState) => {
                return {
                  ...prev,
                  isRunning: false,
                  currentStep: 0,
                  isWaitingForInteraction: false,
                };
              });

              // Cleanup de detecção de ações
              actionDetection.current.cleanupAll();
              currentStepRef.current = 0;

              // Marcar como completado
              TourPersistence.markTourCompleted(userId);
              TourTelemetry.trackTourFinish(defaultConfig.version, userId);

              // Callback externo
              onTourComplete?.();

              // Verificação final do estado
              setTimeout(() => {
                setState((currentState: TourState) => {
                  return currentState; // Não alterar, só verificar
                });
              }, 50);
            }, 2000); // Reduzido para 2 segundos
          }

          // Configurar detecção de ação se necessário
          if (currentStep.data?.requiresInteraction) {
            setupActionDetection(currentStep);
          }
        }

        setState((prev: TourState) => ({
          ...prev,
          currentStep: index,
          isWaitingForInteraction:
            currentStep?.data?.requiresInteraction || false,
        }));
      }

      if (type === "step:before") {
        const currentStep = state.steps[index];

        // Executar função de abertura de painel se necessário
        if (currentStep?.data?.openPanelFn) {
          try {
            currentStep.data.openPanelFn();
          } catch (error) {
            logger.error("[InteractiveTour] Erro ao abrir painel:", error);
            TourTelemetry.trackTourError(
              error as Error,
              defaultConfig.version,
              userId,
              currentStep.id,
              index,
            );
          }
        }
      }
    },
    [
      state.steps,
      skipTour,
      stopTour,
      onTourComplete,
      setupActionDetection,
      defaultConfig.version,
      userId,
    ],
  );

  // Verificar se pode avançar
  const canGoNext = useCallback(() => {
    const currentStep = state.steps[state.currentStep];

    if (!currentStep?.data?.requiresInteraction) {
      return true;
    }

    // Se o step tem progressão manual, sempre permitir avançar
    if (currentStep.data.manualProgression) {
      return true;
    }

    const interactionKey = `${state.currentStep}-${currentStep.data.requiredAction}`;
    return state.completedInteractions.has(interactionKey);
  }, [state.steps, state.currentStep, state.completedInteractions]);

  // Verificar se pode voltar
  const canGoBack = useCallback(() => {
    return state.currentStep > 0;
  }, [state.currentStep]);

  // Reset tour (para desenvolvimento/debug)
  const resetTour = useCallback(() => {
    TourPersistence.resetTour(userId);
    TourTelemetry.clearEvents();
    stopTour();
    logger.info("[InteractiveTour] Tour resetado");
  }, [userId, stopTour]);

  // Cleanup
  useEffect(() => {
    return () => {
      actionDetection.current.cleanupAll();
    };
  }, []);

  // Listener para evento customizado de finalização do tour
  useEffect(() => {
    const handleTourFinish = (event: CustomEvent) => {
      if (state.isRunning) {
        // Finalizar tour
        setState((prev: TourState) => ({
          ...prev,
          isRunning: false,
          isWaitingForInteraction: false,
        }));

        // Salvar como completo
        TourPersistence.saveTourState(
          {
            completed: true,
            version: defaultConfig.version,
            userId,
            completedAt: new Date().toISOString(),
            currentStep: state.currentStep,
            completedSteps: state.steps.map(
              (step: InteractiveTourStep) => step.id,
            ),
          },
          userId,
        );

        // Telemetria
        TourTelemetry.trackEvent("tour_finish", {
          userId,
          reason: event.detail?.reason || "custom-event",
          modalType: event.detail?.modalType,
          currentStep: state.currentStep,
          totalSteps: state.steps.length,
        });

        // Callback
        onTourComplete?.();

        logger.debug(
          "[useInteractiveTour] Tour finalizado com sucesso via evento customizado",
        );
      }
    };

    // Adicionar listener
    window.addEventListener("tour-finish", handleTourFinish as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener(
        "tour-finish",
        handleTourFinish as EventListener,
      );
    };
  }, [
    state.isRunning,
    state.currentStep,
    state.steps.length,
    userId,
    onTourComplete,
  ]);

  // Função para forçar progressão (para casos especiais)
  const forceNextStep = useCallback(() => {
    setState((prev: TourState) => {
      const nextStepIndex = prev.currentStep + 1;

      if (nextStepIndex >= prev.steps.length) {
        return prev; // Não fazer nada se já estiver no último step
      }

      currentStepRef.current = nextStepIndex;

      // Salvar progresso
      const completedStepIds = prev.steps
        .slice(0, nextStepIndex)
        .map((step: InteractiveTourStep) => step.id);
      TourPersistence.saveTourProgress(nextStepIndex, completedStepIds, userId);

      return {
        ...prev,
        currentStep: nextStepIndex,
        isWaitingForInteraction: false,
      };
    });
  }, [userId]);

  return {
    // Estado do tour
    isRunning: state.isRunning,
    currentStep: state.currentStep,
    steps: state.steps,
    isWaitingForInteraction: state.isWaitingForInteraction,
    totalSteps: state.steps.length,

    // Ações principais
    startTour,
    stopTour,
    nextStep,
    previousStep,
    skipTour,
    resetTour,
    forceNextStep, // Nova função para forçar progressão

    // Callback do Joyride
    handleJoyrideCallback,

    // Verificações
    canGoNext: canGoNext(),
    canGoBack: canGoBack(),

    // Utilitários
    getCurrentStep: () => state.steps[state.currentStep] || null,
    getCurrentTarget: () => state.steps[state.currentStep]?.target || "",
    isInteractionCompleted: (stepIndex: number, actionType: string) =>
      state.completedInteractions.has(`${stepIndex}-${actionType}`),

    // Estado de persistência
    isTourCompleted: () => TourPersistence.isTourCompleted(userId),
    wasTourSkipped: () => TourPersistence.wasTourSkipped(userId),
    hasUserInteractedWithTour: () =>
      TourPersistence.hasUserInteractedWithTour(userId),

    // Configuração
    config: defaultConfig,

    // Telemetria
    getTourStats: () => TourTelemetry.getTourStats(),
  };
};

export default useInteractiveTour;
